import type { DataISO, Dipendente, GiornoCalendario } from '../api/tipi.ts';
import { giorniDellIntervallo } from './griglia.ts';

/**
 * Filtri, periodo e ricerca del calendario.
 *
 * Logica pura, testabile senza DOM: decidere quali giorni e quali dipendenti
 * finiscono nella griglia e' la parte che conta, il resto e' posizionamento.
 */

export type Periodo = 'settimana' | 'mese' | 'anno';

export interface FinestraTemporale {
  dal: DataISO;
  al: DataISO;
  etichetta: string;
}

/**
 * La data riportata dentro l'anno mostrato.
 *
 * Il calendario e le assenze si caricano un anno alla volta: una data fuori
 * da quell'anno produce una finestra senza nemmeno un giorno, cioe' una
 * griglia vuota - niente righe, niente colonne - che sembra un guasto e non
 * un anno sbagliato.
 */
export function dentroLAnno(anno: number, data: DataISO): DataISO {
  if (data < `${anno}-01-01`) return `${anno}-01-01`;
  if (data > `${anno}-12-31`) return `${anno}-12-31`;
  return data;
}

/** Lunedi' della settimana che contiene la data. */
export function lunediDi(data: DataISO): DataISO {
  const d = new Date(`${data}T00:00:00Z`);
  // getUTCDay: 0 = domenica. Riportato a 0 = lunedi'.
  const scarto = (d.getUTCDay() + 6) % 7;
  return new Date(d.getTime() - scarto * 86_400_000).toISOString().slice(0, 10);
}

function sommaGiorni(data: DataISO, giorni: number): DataISO {
  return new Date(Date.parse(`${data}T00:00:00Z`) + giorni * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * La finestra visibile, a partire da un ancoraggio e da un passo.
 *
 * L'ancoraggio e' una data qualsiasi dentro il periodo: cosi' avanzare di una
 * settimana o cambiare da settimana a mese non richiede di ricalcolare gli
 * estremi da fuori.
 */
export function finestra(periodo: Periodo, ancora: DataISO, anno: number): FinestraTemporale {
  if (periodo === 'anno') {
    return { dal: `${anno}-01-01`, al: `${anno}-12-31`, etichetta: `Anno ${anno}` };
  }

  // Gli estremi si tagliano sull'anno: la settimana a cavallo di capodanno
  // sporge su giorni che non sono stati caricati, e mostrarne le colonne
  // vuote farebbe sembrare che quelle persone non abbiano niente segnato.
  if (periodo === 'settimana') {
    const dal = dentroLAnno(anno, lunediDi(ancora));
    const al = dentroLAnno(anno, sommaGiorni(lunediDi(ancora), 6));
    return { dal, al, etichetta: `${italiana(dal)} – ${italiana(al)}` };
  }

  const mese = ancora.slice(0, 7);
  const dal = dentroLAnno(anno, `${mese}-01`);
  const ultimo = new Date(Date.UTC(Number(ancora.slice(0, 4)), Number(ancora.slice(5, 7)), 0));
  const al = dentroLAnno(anno, ultimo.toISOString().slice(0, 10));
  return {
    dal,
    al,
    etichetta: capitalizza(
      new Date(`${dal}T00:00:00Z`).toLocaleDateString('it-IT', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    ),
  };
}

/** Sposta l'ancoraggio avanti o indietro di un periodo. */
export function scorri(periodo: Periodo, ancora: DataISO, passi: number): DataISO {
  if (periodo === 'settimana') return sommaGiorni(ancora, passi * 7);
  if (periodo === 'anno') return ancora;

  const anno = Number(ancora.slice(0, 4));
  const mese = Number(ancora.slice(5, 7)) - 1 + passi;
  const d = new Date(Date.UTC(anno, mese, 1));
  return d.toISOString().slice(0, 10);
}

/** I giorni del calendario che cadono dentro la finestra. */
export function giorniNellaFinestra(
  giorni: readonly GiornoCalendario[],
  f: FinestraTemporale,
): GiornoCalendario[] {
  const dentro = new Set(giorniDellIntervallo(f.dal, f.al));
  return giorni.filter((g) => dentro.has(g.data));
}

/**
 * La persona era in forza in almeno un giorno della finestra visibile?
 *
 * Chi ha chiuso il rapporto ad aprile non deve comparire nel calendario di
 * settembre: occuperebbe tre righe vuote in mezzo ai colleghi, e ogni volta
 * qualcuno si chiederebbe perche' non ha mai nulla segnato. Ma deve restare
 * visibile guardando marzo, dove le sue assenze sono storia vera: nasconderlo
 * ovunque falserebbe il passato.
 *
 * Il confronto e' fra stringhe ISO, che per le date in formato AAAA-MM-GG
 * ordina esattamente come il calendario.
 */
export function inForzaNellaFinestra(
  dipendente: Pick<Dipendente, 'dataAssunzione' | 'dataCessazione'>,
  f: FinestraTemporale,
): boolean {
  if (dipendente.dataCessazione !== null && dipendente.dataCessazione < f.dal) return false;
  if (dipendente.dataAssunzione !== null && dipendente.dataAssunzione > f.al) return false;
  return true;
}

export interface Filtri {
  sedeCodice: string | null;
  repartoCodice: string | null;
  ricerca: string;
}

export const FILTRI_VUOTI: Filtri = { sedeCodice: null, repartoCodice: null, ricerca: '' };

/**
 * Applica i filtri all'elenco dipendenti.
 *
 * La ricerca NON filtra: evidenzia. Chi cerca una persona nel calendario vuole
 * posizionarsi sulla sua riga, non perdere di vista tutte le altre — sapere
 * chi altro e' assente quel giorno e' proprio il motivo per cui sta guardando
 * la griglia invece della scheda del singolo.
 */
export function filtraDipendenti(
  dipendenti: readonly Dipendente[],
  filtri: Filtri,
): Dipendente[] {
  return dipendenti.filter(
    (d) =>
      (filtri.sedeCodice === null || d.sedeCodice === filtri.sedeCodice) &&
      (filtri.repartoCodice === null || d.repartoCodice === filtri.repartoCodice),
  );
}

/** Indice del primo dipendente che corrisponde alla ricerca, o -1. */
export function trovaDipendente(
  dipendenti: readonly Dipendente[],
  ricerca: string,
): number {
  const cercato = normalizza(ricerca);
  if (cercato === '') return -1;
  return dipendenti.findIndex((d) => normalizza(d.nomeCompleto).includes(cercato));
}

export function corrisponde(dipendente: Dipendente, ricerca: string): boolean {
  const cercato = normalizza(ricerca);
  return cercato !== '' && normalizza(dipendente.nomeCompleto).includes(cercato);
}

/** Le sedi presenti, e i reparti della sede scelta. */
export function opzioniFiltri(
  dipendenti: readonly Dipendente[],
  sedeCodice: string | null,
): {
  sedi: { codice: string; nome: string }[];
  reparti: { codice: string; nome: string }[];
} {
  const sedi = new Map<string, string>();
  const reparti = new Map<string, string>();
  for (const d of dipendenti) {
    sedi.set(d.sedeCodice, d.sedeNome);
    if (sedeCodice === null || d.sedeCodice === sedeCodice) {
      reparti.set(d.repartoCodice, d.repartoNome);
    }
  }
  const ordina = (m: Map<string, string>) =>
    [...m].map(([codice, nome]) => ({ codice, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  return { sedi: ordina(sedi), reparti: ordina(reparti) };
}

function normalizza(testo: string): string {
  // Toglie gli accenti: chi cerca "nicolo" deve trovare "Nicolò".
  return testo
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

function italiana(data: DataISO): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}`;
}

function capitalizza(testo: string): string {
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}
